import React, { useState, useRef, useEffect } from 'react';
import '../../styles/components/TagMultiSelect.css';

interface OptionGroup {
  label: string;
  options: string[];
}

interface TagMultiSelectProps {
  values: string[];
  onChange: (values: string[]) => void;
  /** Predefined flat options shown in the dropdown */
  options?: string[];
  /** Grouped options — when provided, `options` is ignored */
  groups?: OptionGroup[];
  /** Per-option background colors; chip and dropdown dot will use these */
  optionColors?: Record<string, string>;
  placeholder?: string;
  /** Show a text input at the bottom of the dropdown to add new tags */
  allowCreate?: boolean;
  createLabel?: string;
  /** Max chips to show before collapsing to "+N more" */
  maxVisible?: number;
  /** When true, the trigger cannot be opened and chip/option interactions are blocked */
  disabled?: boolean;
}

const TagMultiSelect: React.FC<TagMultiSelectProps> = ({
  values,
  onChange,
  options = [],
  groups,
  optionColors,
  placeholder = 'Select…',
  allowCreate = true,
  createLabel = 'Add',
  maxVisible,
  disabled = false,
}) => {
  const safeValues = values.filter(v => typeof v === 'string' && v.trim());
  const [open, setOpen] = useState(false);
  // Doubles as the filter query and the new-tag draft — the same input does
  // both, the way SearchableSelect's .ss-search does. It used to be a
  // create-only box pinned to the BOTTOM of the menu, which meant adding a tag
  // required scrolling past every existing option to find it.
  const [query, setQuery] = useState('');
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  // Focus the filter as soon as the menu opens so typing just works, and drop
  // any stale query on close so reopening starts from the full list.
  useEffect(() => {
    if (open) searchRef.current?.focus();
    else setQuery('');
  }, [open]);

  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 10000,
    });
    const onScroll = () => {
      if (!triggerRef.current) return;
      const r = triggerRef.current.getBoundingClientRect();
      setMenuStyle(s => ({ ...s, top: r.bottom + 4, left: r.left, width: r.width }));
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  const toggle = (v: string) => {
    if (disabled) return;
    onChange(safeValues.includes(v) ? safeValues.filter(x => x !== v) : [...safeValues, v]);
  };

  const remove = (v: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    onChange(safeValues.filter(x => x !== v));
  };

  const createNew = () => {
    if (disabled || !allowCreate) return;
    const trimmed = query.trim();
    if (!trimmed) return;
    if (!safeValues.includes(trimmed)) onChange([...safeValues, trimmed]);
    setQuery('');
  };

  // Flat options (with user-created values appended). Case-insensitive
  // dedupe — a stored "chauffeur" shouldn't add a second, near-duplicate
  // entry next to the canonical "Chauffeur" already in `options`.
  const flatOptions = groups
    ? groups.flatMap(g => g.options)
    : [...options, ...safeValues.filter(v => !options.some(o => o.toLowerCase() === v.toLowerCase()))];

  const q = query.trim().toLowerCase();
  const matches = (opt: string) => !q || opt.toLowerCase().includes(q);
  const filteredFlat = flatOptions.filter(matches);
  const filteredGroups = groups
    ? groups.map(g => ({ ...g, options: g.options.filter(matches) })).filter(g => g.options.length > 0)
    : undefined;
  // Only offer to create when the query names something that does not already
  // exist — otherwise Enter should select the existing option, not add a
  // near-duplicate differing only in case.
  const exactExists = flatOptions.some(o => o.toLowerCase() === q);
  const showCreateOption = allowCreate && !!q && !exactExists;
  // The first visible option across either shape, used as Enter's target.
  const firstMatch = filteredGroups ? filteredGroups[0]?.options[0] : filteredFlat[0];

  // Enter: pick the obvious match if there is one, otherwise create. Mirrors
  // SearchableSelect. The list stays open so several tags can be added in a row.
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (firstMatch && !showCreateOption) { toggle(firstMatch); setQuery(''); return; }
    if (showCreateOption) createNew();
  };

  const chipColor = (v: string) => optionColors?.[v];

  // A chip shows whatever casing the option list defines (e.g. "Chauffeur"),
  // even if the stored value was saved lowercase by an older version of this
  // form or a free-typed entry — falls back to capitalizing just the first
  // letter for values with no matching option at all.
  const displayLabel = (v: string): string => {
    const canonical = flatOptions.find(o => o.toLowerCase() === v.toLowerCase());
    if (canonical) return canonical;
    return v.charAt(0).toUpperCase() + v.slice(1);
  };

  const renderOption = (opt: string) => {
    const selected = safeValues.includes(opt);
    const color = chipColor(opt);
    return (
      <div
        key={opt}
        className={`tag-multiselect-option${selected ? ' selected' : ''}`}
        onClick={() => toggle(opt)}
      >
        <span className="tag-ms-option-label">
          {color && (
            <span className="tag-ms-swatch" style={{ /* ds-dynamic: tag color swatch */ background: color }} />
          )}
          {opt}
        </span>
        {selected && <span className="tag-multiselect-check">✓</span>}
      </div>
    );
  };

  return (
    <div className="tag-multiselect" ref={rootRef}>
      <button
        type="button"
        className={`tag-multiselect-trigger form-input${disabled ? ' disabled' : ''}`}
        onClick={() => { if (!disabled) setOpen(o => !o); }}
        aria-expanded={open}
        aria-disabled={disabled}
        disabled={disabled}
        ref={triggerRef}
      >
        {safeValues.length === 0 ? (
          <span className="tag-multiselect-placeholder">{placeholder}</span>
        ) : (
          <div className="tag-multiselect-chips">
            {(maxVisible ? safeValues.slice(0, maxVisible) : safeValues).map(v => {
              const color = chipColor(v);
              return (
                <span
                  key={v}
                  className="tms-chip"
                  style={color ? { background: color, color: '#fff', borderColor: color } : undefined}
                >
                  <span className="tms-chip-label" title={displayLabel(v)}>{displayLabel(v)}</span>
                  <button
                    type="button"
                    className="tms-chip-x"
                    onClick={e => remove(v, e)}
                    aria-label={`Remove ${v}`}
                  >
                    ×
                  </button>
                </span>
              );
            })}
            {maxVisible && safeValues.length > maxVisible && (
              <span className="tms-chip tms-chip-overflow">+{safeValues.length - maxVisible}</span>
            )}
          </div>
        )}
        <span className="tag-multiselect-caret">{open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div className="tag-multiselect-menu" style={menuStyle}>
          <input
            ref={searchRef}
            className="tms-search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={allowCreate ? 'Search or type to add…' : 'Search…'}
            onKeyDown={handleSearchKeyDown}
            onClick={e => e.stopPropagation()}
          />
          <div className="tms-options">
            {filteredGroups ? (
              filteredGroups.map(group => (
                <React.Fragment key={group.label}>
                  <div className="tag-multiselect-group-label">{group.label}</div>
                  {group.options.map(renderOption)}
                </React.Fragment>
              ))
            ) : (
              filteredFlat.map(renderOption)
            )}
            {showCreateOption && (
              <div className="tag-multiselect-option tms-option-create" onClick={createNew}>
                + {createLabel} “{query.trim()}”
              </div>
            )}
            {!showCreateOption && !firstMatch && (
              <div className="tag-multiselect-option tms-option-empty">
                {q ? 'No matches' : 'No options'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TagMultiSelect;
